import styled from 'styled-components';

import { tokens } from '../../../design/tokens';

/**
 * Card de curso — o componente assinatura do sistema.
 *
 * Duas diferenças estruturais em relação ao CourseCardV2:
 *
 * 1. O hover é SÓ transform. O V2 usava `margin-top: -80px`, que é layout:
 *    ele empurrava os cards vizinhos da fileira em vez de crescer sobre eles,
 *    e sem z-index o card crescido ainda ficava POR BAIXO do vizinho.
 *
 * 2. A revelação sobrepõe o pôster; nunca estende a caixa do card. É isso que
 *    a mantém compatível com a fileira em scroll nativo: sem crescimento não
 *    há folga extra a reservar nem sombra de pointer-events invadindo a
 *    fileira de baixo.
 */
export const Card = styled.article`
  --hover-scale: ${tokens.card.hoverScale};
  --hover-lift: ${tokens.card.hoverLift};

  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;

  border-radius: ${tokens.radius.sm};
  background: ${tokens.color.stageBlack};
  transform-origin: center center;

  /* O estado base é o de SAÍDA: sem atraso, o card volta na hora que o
     ponteiro sai. O atraso de entrada mora só na regra de :hover. */
  transition: transform ${tokens.motion.quick} ease 0s,
    box-shadow ${tokens.motion.quick} ease 0s;

  @media (hover: hover) and (pointer: fine) {
    &:hover,
    &:focus-within {
      /* translateY DENTRO de scale é multiplicado pela escala, então o
         deslocamento é dividido para render os 8px pedidos. */
      transform: scale(var(--hover-scale))
        translateY(calc(var(--hover-lift) / var(--hover-scale) * -1));
      box-shadow: ${tokens.shadow.hoverHalo};
      z-index: ${tokens.zIndex.rowHover};

      /* Uma varredura rápida do mouse atravessa a fileira sem acender nada.
         Sem timer, sem estado, sem cleanup — só o atraso da transição. */
      transition-delay: ${tokens.motion.hoverIn};
    }
  }

  /* Nas pontas o card cresce para dentro, não para fora da tela.
     Sem efeito até a fileira (C6) envolver o card num <li>. */
  li:first-child > & {
    transform-origin: left center;
  }

  li:last-child > & {
    transform-origin: right center;
  }
`;

/**
 * Proporção da capa.
 *
 * Desktop mantém 2:1, que é a proporção para a qual a arte dos 24 clientes
 * foi autorada (o V2 usava 260x130) — nenhum recorte novo. Abaixo de 980px
 * vira retrato 3:4, próximo dos 110x150 que o card mobile já praticava.
 *
 * `padding-top` é o fallback de proporção para o piso de navegador do
 * projeto (Android antigo); `aspect-ratio` assume onde existir.
 */
export const Poster = styled.div`
  /*
   * 2:1 em toda largura, alimentado sempre por thumburl.
   *
   * O plano original era retrato no celular usando thumburlvertical. Medido
   * nos dados reais: o campo NÃO é sinal confiável de retrato. Neste tenant
   * os dois cursos têm thumburlvertical preenchido, mas num deles ele aponta
   * para a mesma arte PAISAGEM de 1.92:1 — num quadro 3:4 o "LIVE STREAM"
   * daquela capa virava "IVE / TREAM".
   *
   * Como não dá para saber a proporção do arquivo antes de baixá-lo, e medir
   * em runtime daria cards de alturas diferentes na mesma fileira, o card usa
   * um quadro só e o asset que a escola autorou para exibição em card.
   * thumburlvertical continua servindo o modal de detalhes.
   */
  --ratio-w: 2;
  --ratio-h: 1;

  position: relative;
  overflow: hidden;
  border-radius: ${tokens.radius.sm} ${tokens.radius.sm} 0 0;
  background: ${tokens.color.stageBlack};
  box-shadow: ${tokens.shadow.imageSeat};

  &::before {
    content: '';
    display: block;
    padding-top: calc(100% * var(--ratio-h) / var(--ratio-w));
  }

  @supports (aspect-ratio: 16 / 9) {
    aspect-ratio: var(--ratio-w) / var(--ratio-h);

    &::before {
      display: none;
    }
  }

  > picture,
  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

/**
 * O pôster inteiro é um botão real, com o título do curso como nome
 * acessível. No V2 a capa era uma <img aria-hidden> sem alvo de clique e o
 * modal só abria por um ícone de 22px; aqui o card é exatamente uma parada de
 * tabulação, e focar por teclado dispara o scroll-into-view nativo da fileira.
 */
export const PosterButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;

  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
`;

export const Scrim = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  pointer-events: none;

  opacity: 0;
  transition: opacity ${tokens.motion.quick} ease 0s;

  /* Cobre o pôster inteiro. Uma versão anterior parava em 70% da altura, e o
     bloco revelado é mais alto do que isso — a sinopse caía exatamente na
     faixa ainda transparente e ficava ilegível sobre a arte da capa.
     O topo continua quase limpo, para a capa não desaparecer no hover. */
  background: linear-gradient(
    to top,
    rgba(15, 15, 15, 0.95) 0%,
    rgba(15, 15, 15, 0.88) 38%,
    rgba(15, 15, 15, 0.6) 68%,
    rgba(15, 15, 15, 0.12) 100%
  );

  @media (hover: hover) and (pointer: fine) {
    ${Card}:hover &,
    ${Card}:focus-within & {
      opacity: 1;
      transition-delay: ${tokens.motion.hoverIn};
    }
  }
`;

/**
 * Sinopse e ação, reveladas sobre o pôster.
 *
 * `visibility` transiciona com um passo de 0s atrasado até o fim do fade de
 * saída, e até o início do fade de entrada. É o padrão para "some de verdade":
 * `opacity: 0` sozinho mantém os botões na ordem de tabulação, que é o defeito
 * que o CourseCardMobile tem hoje — no celular, cada card contribui com um
 * "Adicionar ao carrinho" invisível e focável.
 */
export const Reveal = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;

  /* O padding inferior maior reserva a faixa de progresso. */
  padding: ${tokens.space.md} ${tokens.space.md} 22px;

  opacity: 0;
  visibility: hidden;
  transform: translateY(4px);
  transition: opacity ${tokens.motion.quick} ease 0s,
    transform ${tokens.motion.quick} ease 0s,
    visibility 0s linear ${tokens.motion.quick};

  @media (hover: hover) and (pointer: fine) {
    ${Card}:hover &,
    ${Card}:focus-within & {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
      transition: opacity ${tokens.motion.quick} ease ${tokens.motion.hoverIn},
        transform ${tokens.motion.quick} ease ${tokens.motion.hoverIn},
        visibility 0s linear ${tokens.motion.hoverIn};
    }
  }

  .cta-note {
    font-family: ${tokens.font.label.family};
    font-size: ${tokens.font.label.size};
    font-weight: ${tokens.font.label.weight};
    color: ${tokens.color.marqueeGold};
    text-align: center;
  }

  /* Atoms/Button traz altura e margem próprias, dimensionadas para
     formulário. Dentro do card ele precisa caber na capa. */
  > button {
    width: 100%;
    height: auto;
    margin: 0;
    padding: 10px ${tokens.space.md};

    > svg {
      margin-right: ${tokens.space.xs};
      font-size: 18px;
    }
  }
`;

export const Synopsis = styled.p`
  margin-bottom: ${tokens.space.sm};

  font-family: ${tokens.font.caption.family};
  font-size: ${tokens.font.caption.size};
  font-weight: ${tokens.font.caption.weight};
  line-height: 1.45;
  color: ${tokens.color.textMist};

  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
`;

/*
 * Motivo do bloqueio no card. Usa o Ouro de Marquise porque e exatamente "o
 * que precisa ser notado": num card com cadeado, e a unica informacao que
 * diz ao aluno o que fazer a seguir. Tres linhas de teto — as frases reais
 * do produto ("Voce deve completar o curso X para liberar o acesso") cabem
 * com folga, e um texto mais longo trunca em vez de empurrar o botao.
 */
export const BlockedReason = styled.p`
  margin-bottom: ${tokens.space.sm};

  font-family: ${tokens.font.caption.family};
  font-size: ${tokens.font.caption.size};
  font-weight: 600;
  line-height: 1.45;
  color: ${tokens.color.marqueeGold};

  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
`;

export const ProgressStrip = styled.div`
  position: absolute;
  left: ${tokens.space.sm};
  right: ${tokens.space.sm};
  bottom: ${tokens.space.sm};
  z-index: 3;
  pointer-events: none;

  /* Atoms/ProgressBar traz margin-top: 14px para o uso na página do curso.
     Anular aqui, e não no átomo, que é compartilhado. */
  > div {
    margin-top: 0;
  }
`;

/**
 * Faixa de metadados — SEMPRE visível, em repouso e no hover.
 *
 * No V2 título e preço sumiam ao entrar o hover e voltavam dentro do bloco
 * revelado, num flip de estado React. Era a origem do piscar. Aqui eles
 * simplesmente não se movem.
 */
export const Meta = styled.div`
  /* Título em cima, preço embaixo: lado a lado, o preço parcelado ("4x de
     R$49.25", sem quebra) comia a largura e o título virava "B... d..." no
     celular. Empilhado, o título usa a largura inteira do card. */
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${tokens.space.xs};

  padding: 10px 10px 15px;
  border-radius: 0 0 ${tokens.radius.sm} ${tokens.radius.sm};
  background: ${tokens.color.cardCharcoal};
`;

export const MetaTitle = styled.h3`
  font-family: ${tokens.font.body.family};
  font-size: ${tokens.font.body.size};
  font-weight: 600;
  line-height: 1.35;
  color: ${tokens.color.textWhite};

  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
`;

export const MetaPrice = styled.span`
  font-family: ${tokens.font.body.family};
  font-size: ${tokens.font.body.size};
  font-weight: 600;
  white-space: nowrap;
  color: ${tokens.color.marqueeGold};
`;
